import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useAuth } from '../context/AuthContext';
import api, { getAssetUrl } from '../utils/api';
import { toast } from 'react-toastify';
import {
  ArrowLeft, MapPin, ThumbsUp, MessageCircle, CheckCircle,
  Shield, Flag, Send, ChevronUp, ChevronDown
} from 'lucide-react';
import { FramedAvatar, StyledUsername } from '../components/cosmetics';
import ShareButton from '../components/ShareButton';

const formatDate = (ds) => {
  if (!ds) return '';
  return new Date(ds).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
};

export default function PostPage() {
  const { postId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [comments, setComments] = useState([]);

  useEffect(() => {
    fetchPost();
  }, [postId]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/posts/${postId}`);
      setPost(res.data);
      setComments(res.data.comments || []);

      if (user) {
        try {
          const likeRes = await api.get(`/posts/${postId}/liked`);
          setLiked(likeRes.data.liked);
        } catch {}
      }
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Post not found');
        navigate('/');
      } else {
        toast.error('Failed to load post');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!user) { toast.error('Please log in to like posts'); return; }
    try {
      if (liked) {
        await api.delete(`/posts/${postId}/like`);
        setLiked(false);
        setPost(p => ({ ...p, likes: Math.max(0, (p.likes || 1) - 1) }));
      } else {
        await api.post(`/posts/${postId}/like`);
        setLiked(true);
        setPost(p => ({ ...p, likes: (p.likes || 0) + 1 }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update like');
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!user) { toast.error('Please log in to comment'); return; }
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      const res = await api.post(`/posts/${postId}/comments`, { content: newComment.trim() });
      setComments(prev => [...prev, res.data]);
      setPost(p => ({ ...p, comment_count: (p.comment_count || 0) + 1 }));
      setNewComment('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const [voteState, setVoteState] = useState({});

  const handleCommentVote = async (commentId, voteType) => {
    if (!user) { toast.error('Please log in to vote'); return; }
    const current = voteState[commentId];
    const newVote = current === voteType ? null : voteType;
    try {
      const res = await api.post(`/comments/${commentId}/vote`, { vote_type: newVote });
      setVoteState(prev => ({ ...prev, [commentId]: newVote }));
      setComments(prev => prev.map(c =>
        c.id === commentId ? { ...c, upvotes: res.data.upvotes, downvotes: res.data.downvotes } : c
      ));
    } catch {}
  };

  const shareUrl = `/post/${postId}`;
  const shareText = post
    ? `"${(post.content || '').slice(0, 120)}${post.content?.length > 120 ? '...' : ''}" — reported in ${post.ward || 'Regina'} via CYQR.CA`
    : 'Civic issue reported on CYQR.CA';

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
          <div className="text-gray-500 font-mono text-sm tracking-widest animate-pulse">LOADING POST…</div>
        </div>
      </Layout>
    );
  }

  if (!post) return null;

  const daysOutstanding = Math.floor((new Date() - new Date(post.created_at)) / (1000 * 60 * 60 * 24));

  return (
    <Layout>
      <div className="min-h-screen bg-[#0a0a0a]">
        <div className="max-w-2xl mx-auto px-4 py-8">

          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-500 hover:text-white font-mono text-xs tracking-wider transition-colors mb-6"
            data-testid="post-page-back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            BACK TO FEED
          </button>

          {/* Post card */}
          <div className="bg-[#111] border border-zinc-800 rounded-sm overflow-hidden">

            {/* Post header */}
            <div className="p-6 pb-0">
              <div className="flex items-start gap-3 mb-4">
                <FramedAvatar
                  user={{ profile_image_url: post.user_profile_image, profile_frame: post.user_profile_frame, name: post.user_name }}
                  size={10}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/profile/${post.user_id}`} className="font-display text-sm tracking-wider hover:text-[#C5A52A] transition-colors">
                      <StyledUsername user={{ accent_color: post.user_accent_color, name_style: post.user_name_style }}>
                        {post.user_name?.toUpperCase() || 'ANONYMOUS'}
                      </StyledUsername>
                    </Link>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 flex-wrap mt-1">
                    <span className="font-mono text-xs">{formatDate(post.created_at)}</span>
                    {post.ward && (
                      <>
                        <span className="text-gray-700">//</span>
                        <span className="font-mono text-xs text-[#C5A52A]">{post.ward}</span>
                      </>
                    )}
                    {post.resolved_at && (
                      <>
                        <span className="text-gray-700">//</span>
                        <span className="font-mono text-xs text-green-500 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> RESOLVED
                        </span>
                      </>
                    )}
                    {!post.resolved_at && post.verification_status === 'fixed' && (
                      <>
                        <span className="text-gray-700">//</span>
                        <span className="font-mono text-xs text-emerald-400 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" /> VERIFIED FIXED
                        </span>
                      </>
                    )}
                    {post.verification_status === 'still_issue' && (
                      <>
                        <span className="text-gray-700">//</span>
                        <span className="font-mono text-xs text-orange-400">STILL AN ISSUE</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Post ID + days outstanding badge */}
                <div className="text-right flex-shrink-0">
                  <div className="font-mono text-[10px] text-gray-600">#{post.id.slice(0, 8).toUpperCase()}</div>
                  <div className="font-mono text-[10px] text-gray-600">{daysOutstanding}d outstanding</div>
                </div>
              </div>

              {/* Content */}
              <blockquote className="text-gray-100 leading-loose text-lg sm:text-xl tracking-wide mb-4">
                "{post.content}"
              </blockquote>

              {/* Address */}
              {post.address && (
                <div className="flex items-center gap-2 text-gray-500 mb-4">
                  <MapPin className="w-3.5 h-3.5" />
                  <span className="font-mono text-xs">{post.address}</span>
                </div>
              )}
            </div>

            {/* Image */}
            {post.image_url && (
              <div className="mx-6 mb-4 rounded overflow-hidden border border-zinc-800">
                <img
                  src={post.image_url.startsWith('http') ? post.image_url : getAssetUrl(post.image_url)}
                  alt="Post attachment"
                  className="w-full h-auto"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}

            {/* Footer: badges + actions */}
            <div className="px-6 pb-4 border-t border-zinc-800 pt-4">
              <div className="flex items-center justify-between">
                {/* User badges */}
                <div className="flex items-center gap-2">
                  {post.user_badges?.slice(0, 3).map((badge, i) => (
                    <div key={badge.id || badge.slug || `badge-${i}`} className="relative group" title={badge.name}>
                      {badge.icon && badge.icon.startsWith('http') ? (
                        <img src={badge.icon} alt={badge.name} className="w-9 h-9 rounded object-cover border border-zinc-700 hover:border-[#C5A52A] transition-colors" />
                      ) : (
                        <div className="w-9 h-9 rounded bg-zinc-800 border border-zinc-700 flex items-center justify-center text-base">{badge.icon || '🏅'}</div>
                      )}
                    </div>
                  ))}
                  {post.user_badges?.length > 3 && (
                    <div className="w-9 h-9 rounded bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                      <span className="text-xs text-zinc-400 font-mono">+{post.user_badges.length - 3}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleLike}
                    className={`flex items-center gap-1.5 transition-colors ${liked ? 'text-[#C5A52A]' : 'text-gray-600 hover:text-[#C5A52A]'}`}
                    data-testid="post-page-like-btn"
                  >
                    <ThumbsUp className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
                    <span className="font-mono text-xs">{post.likes || 0}</span>
                  </button>
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <MessageCircle className="w-4 h-4" />
                    <span className="font-mono text-xs">{comments.length}</span>
                  </div>
                  <ShareButton url={shareUrl} text={shareText} label="SHARE" size="xs" />
                </div>
              </div>
            </div>
          </div>

          {/* Comments section */}
          <div className="mt-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px flex-1 bg-zinc-800" />
              <span className="font-mono text-xs text-gray-600 tracking-widest">COMMENTS ({comments.length})</span>
              <div className="h-px flex-1 bg-zinc-800" />
            </div>

            {/* Add comment */}
            {user ? (
              <form onSubmit={handleComment} className="mb-6">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment…"
                    maxLength={500}
                    className="flex-1 bg-[#111] border border-zinc-700 rounded-sm px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C5A52A] font-mono"
                    data-testid="post-page-comment-input"
                  />
                  <button
                    type="submit"
                    disabled={submitting || !newComment.trim()}
                    className="bg-[#C5A52A] hover:bg-[#d4b52e] disabled:opacity-40 text-black px-4 py-2 rounded-sm transition-colors flex items-center gap-1.5"
                    data-testid="post-page-comment-submit"
                  >
                    <Send className="w-3.5 h-3.5" />
                    <span className="font-mono text-xs font-bold">POST</span>
                  </button>
                </div>
              </form>
            ) : (
              <div className="mb-6 p-3 bg-[#111] border border-zinc-800 rounded-sm text-center">
                <Link to="/login" className="text-[#C5A52A] font-mono text-xs hover:underline">Log in to comment</Link>
              </div>
            )}

            {/* Comment list */}
            <div className="space-y-3">
              {comments.length === 0 && (
                <div className="text-center py-8 text-gray-600 font-mono text-xs tracking-widest">
                  NO COMMENTS YET — BE FIRST
                </div>
              )}
              {comments.map((comment) => (
                <div key={comment.id} className="bg-[#0a0a0a] border border-zinc-800 rounded-sm p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="font-display text-xs text-gray-300 tracking-wider">{comment.user_name?.toUpperCase()}</span>
                      <span className="text-gray-700 text-xs font-mono">{formatDate(comment.created_at)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleCommentVote(comment.id, 'up')}
                        className={`flex items-center gap-1 transition-colors ${voteState[comment.id] === 'up' ? 'text-[#C5A52A]' : 'text-gray-600 hover:text-gray-400'}`}
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                        <span className="font-mono text-[10px]">{comment.upvotes || 0}</span>
                      </button>
                      <button
                        onClick={() => handleCommentVote(comment.id, 'down')}
                        className={`flex items-center gap-1 transition-colors ${voteState[comment.id] === 'down' ? 'text-red-400' : 'text-gray-600 hover:text-gray-400'}`}
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                        <span className="font-mono text-[10px]">{comment.downvotes || 0}</span>
                      </button>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">{comment.content}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
}
